/**
 * Standardized Loading State Component
 * 
 * Provides consistent loading states across the app with:
 * - Skeleton loaders for content
 * - Full-screen loading states
 * - Inline loading indicators
 * - Success animations
 */

import React, { useEffect, useRef } from 'react';
import { View, ActivityIndicator, Animated, Easing } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '@/theme';
import { ThemedText, Body, Caption } from '@/components/ui/Text';

type LoadingStateProps = {
  variant?: 'fullscreen' | 'inline' | 'skeleton';
  message?: string;
  size?: 'small' | 'large';
  showIcon?: boolean;
  accessibilityLabel?: string;
  accessibilityLiveRegion?: 'none' | 'polite' | 'assertive';
};

export function LoadingState({
  variant = 'inline',
  message,
  size = 'large',
  showIcon = true,
  accessibilityLabel,
  accessibilityLiveRegion = 'polite',
}: LoadingStateProps) {
  const theme = useTheme();

  if (variant === 'fullscreen') {
    return (
      <View
        style={{
          flex: 1,
          width: '100%',
          alignSelf: 'stretch',
          justifyContent: 'center',
          alignItems: 'center',
          padding: theme.spacing(4),
          gap: theme.spacing(2),
        }}
        accessibilityLabel={accessibilityLabel || message || 'Loading'}
        accessibilityLiveRegion={accessibilityLiveRegion}
        accessibilityRole="progressbar"
      >
        {showIcon && (
          <ActivityIndicator 
            size={size} 
            color={theme.colors.primary}
            accessibilityLabel={accessibilityLabel || 'Loading'}
          />
        )}
        {message && (
          <Body color={theme.colors.textSecondary} style={{ textAlign: 'center' }}>
            {message}
          </Body>
        )}
      </View>
    );
  }

  if (variant === 'inline') {
    return (
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: theme.spacing(1.5),
          padding: theme.spacing(2),
        }}
        accessibilityLabel={accessibilityLabel || message || 'Loading'}
        accessibilityLiveRegion={accessibilityLiveRegion}
        accessibilityRole="progressbar"
      >
        {showIcon && (
          <ActivityIndicator 
            size={size} 
            color={theme.colors.primary}
            accessibilityLabel={accessibilityLabel || 'Loading'}
          />
        )}
        {message && (
          <Body color={theme.colors.textSecondary}>{message}</Body>
        )}
      </View>
    );
  }

  return null;
}

/**
 * Success Animation Component
 * Shows a checkmark animation when an action completes successfully
 */
type SuccessAnimationProps = {
  visible: boolean;
  message?: string;
  onComplete?: () => void;
};

export function SuccessAnimation({
  visible,
  message,
  onComplete,
}: SuccessAnimationProps) {
  const theme = useTheme();
  const scaleAnim = useRef(new Animated.Value(0)).current;
  const opacityAnim = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    if (visible) {
      // Reset animations
      scaleAnim.setValue(0);
      opacityAnim.setValue(0);

      // Animate in
      Animated.parallel([
        Animated.spring(scaleAnim, {
          toValue: 1,
          tension: 100,
          friction: 6,
          useNativeDriver: true,
        }),
        Animated.timing(opacityAnim, {
          toValue: 1,
          duration: 200,
          easing: Easing.out(Easing.ease),
          useNativeDriver: true,
        }),
      ]).start();

      // Call onComplete after animation
      if (onComplete) {
        setTimeout(() => {
          onComplete();
        }, 2000);
      }
    }
  }, [visible, scaleAnim, opacityAnim, onComplete]);

  if (!visible) return null;

  return (
    <Animated.View
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: theme.colors.modalOverlay,
        justifyContent: 'center',
        alignItems: 'center',
        opacity: opacityAnim,
        zIndex: 1000,
      }}
    >
      <Animated.View
        style={{
          backgroundColor: theme.colors.surface,
          borderRadius: theme.radii.xl,
          padding: theme.spacing(4),
          alignItems: 'center',
          gap: theme.spacing(2),
          minWidth: 260,
          maxWidth: 320,
          transform: [{ scale: scaleAnim }],
          ...theme.shadows.level3,
        }}
      >
        <View
          style={{
            width: 72,
            height: 72,
            borderRadius: 36,
            backgroundColor: theme.colors.success || theme.colors.primary,
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Ionicons name="checkmark" size={44} color="#ffffff" />
        </View>
        {message && (
          <View style={{ alignItems: 'center', gap: theme.spacing(0.5) }}>
            <Body style={{ textAlign: 'center', fontWeight: '700', fontSize: 18 }}>
              {message.split('\n')[0]}
            </Body>
            {message.includes('\n') && (
              <Caption style={{ textAlign: 'center', color: theme.colors.textSecondary }}>
                {message.split('\n').slice(1).join('\n')}
              </Caption>
            )}
          </View>
        )}
      </Animated.View>
    </Animated.View>
  );
}


